import RetreatService from "features/retiro/retiroService";
import { useEffect, useMemo, useState } from "react";
import FarmService from "services/FazendaService";
import { getSelectedClientFromStorage } from "shared/storageUtils";
import type { FarmAndRetreatLink } from "../../../../userManagement.types";

type Option = {
  label: string;
  value: number;
};

type RetreatOption = Option & {
  farmId: number;
};

type UseFarmAndRetreatsLinksProps = {
  formData: FarmAndRetreatLink[];
  selectedFarmId: number | null;
};

export function useFarmAndRetreatsOptions({
  formData,
  selectedFarmId,
}: UseFarmAndRetreatsLinksProps) {
  const [isFetchingOptions, setIsFetchingOptions] = useState<boolean>(true);
  const [options, setOptions] = useState<{
    farms: Option[];
    retreats: RetreatOption[];
  }>({ farms: [], retreats: [] });

  const selectedClientId = useMemo(() => getSelectedClientFromStorage(), []);

  const fetchData = async (clientId: number) => {
    setIsFetchingOptions(true);

    const farmsResponse = await FarmService.listarFazendasPorCliente(clientId);
    const farms: Option[] =
      //@ts-expect-error: API return type is not typed yet
      farmsResponse.data?.map((farm) => ({
        value: farm.IdFazenda,
        label: farm.Nome,
      })) ?? [];

    const retreatsResponse = await RetreatService.listaRetirosPorIdsFazenda(
      farms.map((farm) => farm.value)
    );
    const retreats: RetreatOption[] =
      //@ts-expect-error: API return type is not typed yet
      retreatsResponse?.map((retreat) => ({
        value: retreat.IdRetiro,
        label: retreat.Nome,
        farmId: retreat.IdFazenda,
      })) ?? [];

    setOptions({ farms, retreats });
    setIsFetchingOptions(false);
  };

  useEffect(() => {
    if (selectedClientId == null) {
      return;
    }

    fetchData(selectedClientId);
  }, [selectedClientId]);

  const filteredFarmOptions = useMemo(() => {
    const linkedFarmIds = formData.map((link) => link.farm.id);
    return options.farms.filter((farm) => !linkedFarmIds.includes(farm.value));
  }, [formData, options.farms]);

  const filteredRetreatOptions = useMemo(
    () =>
      selectedFarmId != null
        ? options.retreats.filter(
            (retreat) => retreat.farmId === selectedFarmId
          )
        : [],
    [options.retreats, selectedFarmId]
  );

  return {
    isFetchingOptions,
    farmOptions: filteredFarmOptions,
    retreatOptions: filteredRetreatOptions,
  };
}
